import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Wheat,
  Truck,
  ArrowRightLeft,
  Plus,
  Paperclip,
  Trash2,
  AlertCircle,
  Building2,
  Layers,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PesoIcon } from '@/components/common/PesoIcon';
import { useAuth } from '@/context/AuthContext';
import { feedService } from '@/services/feedService';
import { farmService } from '@/services/farmService';
import { cycleService } from '@/services/cycleService';
import { BulkFeedInvoiceModal } from '@/components/feed/BulkFeedInvoiceModal';
import { FeedTransferModal } from '@/components/feed/FeedTransferModal';
import { FeedDeliveryModal } from '@/components/feed/FeedDeliveryModal';
import { SoftVoidConfirmModal } from '@/components/common/SoftVoidConfirmModal';
import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { PageHeader } from '@/components/common/PageHeader';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { YearSelector } from '@/components/reports/YearSelector';
import { KpiCardGroup } from '@/components/common/KpiCardGroup';
import { KpiCard } from '@/components/common/KpiCard';
import { FilterBar } from '@/components/common/FilterBar';
import { Combobox } from '@/components/common/Combobox';
import { formatDate, formatCurrency } from '@/lib/utils';
import {
  FeedReceiptRecord,
  FeedTransferRecord,
  FarmRecord,
  ProductionCycleRecord,
} from '@/types/database';

export const FeedManagementPage: React.FC = () => {
  const { isOperations } = useAuth();

  // Active Tab: 2-tab direct operating architecture (Bodega removed)
  const [activeTab, setActiveTab] = useState<'receipts' | 'transfers'>('receipts');
  const [loading, setLoading] = useState<boolean>(true);

  // Data states
  const [receipts, setReceipts] = useState<FeedReceiptRecord[]>([]);
  const [transfers, setTransfers] = useState<FeedTransferRecord[]>([]);
  const [farms, setFarms] = useState<FarmRecord[]>([]);
  const [cycles, setCycles] = useState<ProductionCycleRecord[]>([]);

  // Year filter state (defaults to latest year with data)
  const [selectedYear, setSelectedYear] = useState<string>('2026');

  // Filter states
  const [selectedFarmFilter, setSelectedFarmFilter] = useState<string>('');
  const [selectedCycleFilter, setSelectedCycleFilter] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Expandable row drawer states
  const [expandedReceiptId, setExpandedReceiptId] = useState<string | null>(null);
  const [expandedTransferId, setExpandedTransferId] = useState<string | null>(null);

  // Modal states
  const [showBulkInvoiceModal, setShowBulkInvoiceModal] = useState<boolean>(false);
  const [showTransferModal, setShowTransferModal] = useState<boolean>(false);
  const [showSingleDeliveryModal, setShowSingleDeliveryModal] = useState<boolean>(false);

  // Soft-void modal state
  const [voidModalState, setVoidModalState] = useState<{
    isOpen: boolean;
    tableName: 'feed_receipts' | 'feed_transfers';
    recordId: string;
    recordIdentifier: string;
  }>({
    isOpen: false,
    tableName: 'feed_receipts',
    recordId: '',
    recordIdentifier: '',
  });

  // Document preview state
  const [previewDoc, setPreviewDoc] = useState<any | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [recsRes, transRes, farmsRes, cycsRes] = await Promise.all([
        feedService.getFeedReceipts({
          farmId: selectedFarmFilter || undefined,
          cycleId: selectedCycleFilter || undefined,
        }),
        feedService.getFeedTransfers({
          cycleId: selectedCycleFilter || undefined,
        }),
        farmService.getActiveFarms(),
        cycleService.getCycles(),
      ]);

      setReceipts(recsRes);
      setTransfers(transRes);
      setFarms(farmsRes);
      setCycles(cycsRes);
    } catch (err: any) {
      console.error('Error loading feed data:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedFarmFilter, selectedCycleFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derive available years dynamically from live receipt and transfer records
  const availableYears = useMemo(() => {
    const yearsSet = new Set<string>();
    receipts.forEach((r) => {
      if (r.receipt_date) {
        const yr = r.receipt_date.substring(0, 4);
        if (/^\d{4}$/.test(yr)) yearsSet.add(yr);
      }
    });
    transfers.forEach((t) => {
      if (t.transfer_date) {
        const yr = t.transfer_date.substring(0, 4);
        if (/^\d{4}$/.test(yr)) yearsSet.add(yr);
      }
    });

    if (yearsSet.size === 0) {
      yearsSet.add('2026');
      yearsSet.add('2025');
      yearsSet.add('2024');
    }

    const sorted = Array.from(yearsSet).sort((a, b) => b.localeCompare(a));
    return sorted;
  }, [receipts, transfers]);

  // Synchronize default year if current selected is not in list
  useEffect(() => {
    if (availableYears.length > 0 && selectedYear !== 'all' && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Year-filtered Receipts
  const yearFilteredReceipts = useMemo(() => {
    if (selectedYear === 'all') return receipts;
    return receipts.filter((r) => r.receipt_date?.startsWith(selectedYear));
  }, [receipts, selectedYear]);

  // Year-filtered Transfers
  const yearFilteredTransfers = useMemo(() => {
    if (selectedYear === 'all') return transfers;
    return transfers.filter((t) => t.transfer_date?.startsWith(selectedYear));
  }, [transfers, selectedYear]);

  // Calculations for KPI Summary based on year-filtered data
  const totalAllocatedBags = useMemo(() => {
    return yearFilteredReceipts.reduce((sum, r) => sum + (Number(r.quantity_bags) || 0), 0);
  }, [yearFilteredReceipts]);

  const totalAllocatedCost = useMemo(() => {
    return yearFilteredReceipts.reduce((sum, r) => sum + (Number(r.total_cost) || 0), 0);
  }, [yearFilteredReceipts]);

  const totalTransfersCount = useMemo(() => {
    return yearFilteredTransfers.length;
  }, [yearFilteredTransfers]);

  const totalTransferredBags = useMemo(() => {
    return yearFilteredTransfers.reduce((sum, t) => sum + (Number(t.quantity_bags) || 0), 0);
  }, [yearFilteredTransfers]);

  // Search-filtered Receipts
  const filteredReceipts = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return yearFilteredReceipts;

    return yearFilteredReceipts.filter((r) => {
      const inv = r.invoice_no?.toLowerCase() || '';
      const dr = r.delivery_receipt_no?.toLowerCase() || '';
      const sup = r.suppliers?.supplier_name?.toLowerCase() || '';
      const cycle = r.production_cycles?.cycle_code?.toLowerCase() || '';
      const grower = r.production_cycles?.growers?.full_name?.toLowerCase() || '';
      const prod = r.feed_products?.product_name?.toLowerCase() || '';
      return (
        inv.includes(term) ||
        dr.includes(term) ||
        sup.includes(term) ||
        cycle.includes(term) ||
        grower.includes(term) ||
        prod.includes(term)
      );
    });
  }, [yearFilteredReceipts, searchTerm]);

  // Search-filtered Transfers
  const filteredTransfers = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return yearFilteredTransfers;

    return yearFilteredTransfers.filter((t) => {
      const src = t.source_cycle?.cycle_code?.toLowerCase() || '';
      const srcGrower = t.source_cycle?.growers?.full_name?.toLowerCase() || '';
      const dst = t.destination_cycle?.cycle_code?.toLowerCase() || '';
      const dstGrower = t.destination_cycle?.growers?.full_name?.toLowerCase() || '';
      const prod = t.feed_products?.product_name?.toLowerCase() || '';
      const notes = t.notes?.toLowerCase() || '';
      return (
        src.includes(term) ||
        srcGrower.includes(term) ||
        dst.includes(term) ||
        dstGrower.includes(term) ||
        prod.includes(term) ||
        notes.includes(term)
      );
    });
  }, [yearFilteredTransfers, searchTerm]);

  const handleSoftVoidConfirm = async (reason: string) => {
    if (voidModalState.tableName === 'feed_receipts') {
      await feedService.softVoidFeedReceipt(voidModalState.recordId, reason);
    } else {
      await feedService.softVoidFeedTransfer(voidModalState.recordId, reason);
    }
    setVoidModalState({ isOpen: false, tableName: 'feed_receipts', recordId: '', recordIdentifier: '' });
    loadData();
  };

  // Reset all filters
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedCycleFilter('');
    setSelectedFarmFilter('');
  };

  // Cycle Combobox Options
  const cycleOptions = useMemo(() => {
    return cycles.map((c) => ({
      value: c.id,
      label: `${c.cycle_code} — ${c.growers?.full_name || 'Company-Owned'}`,
      sublabel: `Status: ${c.status}`,
    }));
  }, [cycles]);

  // Farm Combobox Options
  const farmOptions = useMemo(() => {
    return farms.map((f) => ({
      value: f.id,
      label: f.farm_name,
    }));
  }, [farms]);

  return (
    <div className="space-y-5 max-w-7xl mx-auto pb-12">
      {/* 1. Standardized Page Header */}
      <PageHeader
        title={selectedYear === 'all' ? 'Feed Management — All Years' : `Feed Management — ${selectedYear}`}
        subtitle="Supplier → Production Cycle direct deliveries and cycle-to-cycle feed transfers."
        icon={Wheat}
        helpScreenKey="feed"
        actions={
          <>
            <ActionTooltip content="Reload feed delivery receipts, transfer records, and inventory totals.">
              <Button
                variant="outline"
                size="sm"
                onClick={loadData}
                className="text-xs h-8 sm:h-9 text-slate-700 hover:bg-slate-50 gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5 text-slate-500" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </ActionTooltip>

            {isOperations && (
              <>
                <ActionTooltip content="Move unused feed bags from one batch to another with zero net farm cost impact.">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowTransferModal(true)}
                    className="text-xs h-8 sm:h-9 gap-1.5 border-purple-300 text-purple-900 bg-purple-50/50 hover:bg-purple-100 font-semibold"
                  >
                    <ArrowRightLeft className="w-3.5 h-3.5 text-purple-700" />
                    <span>Transfer Feed</span>
                  </Button>
                </ActionTooltip>

                <ActionTooltip content="Record an individual supplier feed delivery ticket directly to one batch.">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowSingleDeliveryModal(true)}
                    className="text-xs h-8 sm:h-9 gap-1.5 border-slate-300 text-slate-700 hover:bg-slate-50 hidden md:inline-flex"
                  >
                    <Plus className="w-3.5 h-3.5 text-slate-600" />
                    <span>Single Delivery</span>
                  </Button>
                </ActionTooltip>

                <ActionTooltip content="Allocate a master supplier feed delivery invoice across multiple active batches.">
                  <Button
                    size="sm"
                    onClick={() => setShowBulkInvoiceModal(true)}
                    className="text-xs h-8 sm:h-9 bg-emerald-700 hover:bg-emerald-600 text-white font-semibold gap-1.5 shadow-xs"
                  >
                    <Layers className="w-3.5 h-3.5 text-emerald-200" />
                    <span>Bulk Feed Invoice</span>
                  </Button>
                </ActionTooltip>
              </>
            )}
          </>
        }
        yearSelector={
          <YearSelector
            availableYears={availableYears}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        }
      />

      {/* 2. Standardized KPI Summary Cards (4 Cards) */}
      <KpiCardGroup columns={4}>
        <KpiCard
          title="Direct Deliveries"
          value={`${yearFilteredReceipts.length.toLocaleString()} receipts`}
          subtitle={selectedYear === 'all' ? 'All historical allocations' : `${yearFilteredReceipts.length} in ${selectedYear}`}
          icon={Truck}
          variant="default"
        />
        <KpiCard
          title="Total Feed Allocated"
          value={`${totalAllocatedBags.toLocaleString()} bags`}
          subtitle={`${(totalAllocatedBags * 50).toLocaleString()} kg gross weight`}
          icon={Wheat}
          variant="emerald"
        />
        <KpiCard
          title="Booked Feed Cost"
          value={formatCurrency(totalAllocatedCost)}
          subtitle="Base price + markup + trucking"
          icon={PesoIcon}
          variant="default"
        />
        <KpiCard
          title="Cycle Transfers"
          value={`${totalTransfersCount} transfers`}
          subtitle={`${totalTransferredBags.toLocaleString()} bags reallocated`}
          icon={ArrowRightLeft}
          variant="indigo"
        />
      </KpiCardGroup>

      {/* 3. View Switcher Tabs & Filter Bar */}
      <div className="space-y-3">
        {/* Segmented View Switcher Tabs */}
        <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
          <button
            onClick={() => setActiveTab('receipts')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'receipts'
                ? 'bg-emerald-800 text-white shadow-sm ring-1 ring-emerald-900/10'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <Truck className="w-3.5 h-3.5" />
            <span>Direct Feed Receipts & Invoices</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                activeTab === 'receipts' ? 'bg-emerald-900 text-emerald-100' : 'bg-slate-200 text-slate-700'
              }`}
            >
              {yearFilteredReceipts.length}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('transfers')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === 'transfers'
                ? 'bg-purple-800 text-white shadow-sm ring-1 ring-purple-900/10'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <ArrowRightLeft className="w-3.5 h-3.5" />
            <span>Cycle-to-Cycle Feed Transfers</span>
            <span
              className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono ${
                activeTab === 'transfers' ? 'bg-purple-900 text-purple-100' : 'bg-slate-200 text-slate-700'
              }`}
            >
              {yearFilteredTransfers.length}
            </span>
          </button>
        </div>

        {/* Standardized FilterBar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder={
            activeTab === 'receipts'
              ? 'Search invoice, DR, supplier, cycle...'
              : 'Search source, destination, product...'
          }
          onReset={handleResetFilters}
          showReset={Boolean(searchTerm || selectedCycleFilter || selectedFarmFilter)}
        >
          {/* Production Cycle Combobox */}
          <div className="w-full sm:w-64">
            <Combobox
              options={cycleOptions}
              value={selectedCycleFilter}
              onChange={setSelectedCycleFilter}
              allowAll={true}
              allLabel="All Production Cycles"
              placeholder="Filter by Cycle..."
              searchPlaceholder="Search cycle code or grower..."
            />
          </div>

          {/* Farm Location Combobox */}
          <div className="w-full sm:w-52">
            <Combobox
              options={farmOptions}
              value={selectedFarmFilter}
              onChange={setSelectedFarmFilter}
              allowAll={true}
              allLabel="All Farm Locations"
              placeholder="Filter by Farm..."
              searchPlaceholder="Search farm name..."
            />
          </div>
        </FilterBar>

        {/* 4. Tab 1 Content: Direct Feed Receipts & Invoices */}
        {activeTab === 'receipts' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-slate-400 text-xs font-medium">
                Loading feed receipts from live database...
              </div>
            ) : filteredReceipts.length === 0 ? (
              <div className="p-12 text-center text-slate-500 text-xs">
                No direct feed receipts found matching the filter criteria.
              </div>
            ) : (
              <>
                {/* Desktop 8-Column Progressive Disclosure Table (md:table) */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse font-sans">
                    <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3 whitespace-nowrap w-24">Date</th>
                        <th className="py-2.5 px-3 whitespace-nowrap">Invoice / DR #</th>
                        <th className="py-2.5 px-3">Supplier</th>
                        <th className="py-2.5 px-3">Destination Cycle & Raiser</th>
                        <th className="py-2.5 px-3">Feed Product</th>
                        <th className="py-2.5 px-3 text-right whitespace-nowrap">Bags</th>
                        <th className="py-2.5 px-3 text-right whitespace-nowrap">Total Cost</th>
                        <th className="py-2.5 px-2 text-center w-12">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredReceipts.map((r) => {
                        const isExpanded = expandedReceiptId === r.id;
                        const markupTrucking = (Number(r.feed_markup_amount) || 0) + (Number(r.trucking_cost) || 0);
                        const hasDoc = r.documents && r.documents.length > 0;

                        return (
                          <React.Fragment key={r.id}>
                            <tr
                              onClick={() => setExpandedReceiptId(isExpanded ? null : r.id)}
                              className={`cursor-pointer transition-colors ${
                                isExpanded ? 'bg-emerald-50/60' : 'hover:bg-slate-50'
                              }`}
                            >
                              <td className="py-2.5 px-3 font-mono text-slate-600 whitespace-nowrap">
                                {formatDate(r.receipt_date)}
                              </td>
                              <td className="py-2.5 px-3 font-mono font-semibold text-slate-900 whitespace-nowrap">
                                {r.invoice_no ? (
                                  <span>Inv #{r.invoice_no}</span>
                                ) : r.delivery_receipt_no ? (
                                  <span>DR #{r.delivery_receipt_no}</span>
                                ) : (
                                  <span className="text-slate-400 font-normal">{r.receipt_number || '—'}</span>
                                )}
                              </td>
                              <td className="py-2.5 px-3 font-medium text-slate-800 truncate max-w-[160px]">
                                {r.suppliers?.supplier_name || 'Direct Supplier'}
                              </td>
                              <td className="py-2.5 px-3">
                                <span className="font-mono font-semibold text-slate-900 block">
                                  {r.production_cycles?.cycle_code || 'Unassigned Cycle'}
                                </span>
                                <span className="text-[11px] text-slate-500 block truncate max-w-[180px]">
                                  {r.production_cycles?.growers?.full_name || 'Company-Owned'}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-slate-700 truncate max-w-[160px]">
                                {r.feed_products?.product_name || 'Standard Feed'}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                                {Number(r.quantity_bags).toLocaleString()}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                                {formatCurrency(Number(r.total_cost))}
                              </td>
                              <td className="py-2.5 px-2 text-center text-slate-400">
                                <ChevronDown
                                  className={`w-4 h-4 mx-auto transition-transform duration-150 ${
                                    isExpanded ? 'transform rotate-180 text-emerald-700' : ''
                                  }`}
                                />
                              </td>
                            </tr>

                            {/* Progressive Disclosure Row Drawer */}
                            {isExpanded && (
                              <tr className="bg-slate-50/90 border-b border-slate-200">
                                <td colSpan={8} className="p-3.5 sm:p-4">
                                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-white p-3 rounded-lg border border-slate-200 shadow-inner">
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Unit Price</span>
                                      <span className="font-mono font-semibold text-slate-800">
                                        ₱{Number(r.unit_price).toFixed(2)} / bag
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Markup & Trucking</span>
                                      <span className="font-mono text-slate-800">
                                        {markupTrucking > 0 ? `₱${markupTrucking.toFixed(2)}` : '₱0.00'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Gross Weight</span>
                                      <span className="font-mono text-slate-800">
                                        {(Number(r.quantity_bags) * 50).toLocaleString()} kg
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Receipt Reference</span>
                                      <span className="font-mono text-slate-700">{r.receipt_number || r.id}</span>
                                    </div>
                                  </div>

                                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-200 text-xs">
                                    <div className="flex items-center gap-2">
                                      {hasDoc ? (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setPreviewDoc(r.documents![0]);
                                          }}
                                          className="text-xs h-7 gap-1 border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
                                        >
                                          <Paperclip className="w-3.5 h-3.5 text-emerald-700" /> View Attached Slip
                                        </Button>
                                      ) : (
                                        <span className="text-slate-400 italic text-[11px]">No attachment on file</span>
                                      )}
                                      {r.notes && <span className="text-slate-500 italic">Notes: {r.notes}</span>}
                                    </div>

                                    {isOperations && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setVoidModalState({
                                            isOpen: true,
                                            tableName: 'feed_receipts',
                                            recordId: r.id,
                                            recordIdentifier: `Receipt #${r.receipt_number || r.invoice_no || r.id}`,
                                          });
                                        }}
                                        className="text-xs h-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50 gap-1"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" /> Soft-Void
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Responsive Cards Layout (md:hidden) */}
                <div className="md:hidden divide-y divide-slate-200">
                  {filteredReceipts.map((r) => {
                    const isExpanded = expandedReceiptId === r.id;
                    const hasDoc = r.documents && r.documents.length > 0;

                    return (
                      <div
                        key={r.id}
                        onClick={() => setExpandedReceiptId(isExpanded ? null : r.id)}
                        className="p-3.5 space-y-2 hover:bg-slate-50 cursor-pointer"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-mono text-slate-500">{formatDate(r.receipt_date)}</span>
                          <span className="text-sm font-bold font-mono text-slate-900">
                            {formatCurrency(Number(r.total_cost))}
                          </span>
                        </div>

                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-bold text-slate-900">
                              {r.invoice_no ? `Inv #${r.invoice_no}` : r.delivery_receipt_no ? `DR #${r.delivery_receipt_no}` : r.receipt_number}
                            </p>
                            <p className="text-xs text-slate-600 font-medium">{r.suppliers?.supplier_name}</p>
                            <p className="text-[11px] text-slate-500">
                              {r.production_cycles?.cycle_code} • {r.production_cycles?.growers?.full_name || 'Company-Owned'}
                            </p>
                          </div>
                          <span className="px-2 py-1 bg-emerald-50 text-emerald-800 rounded font-mono font-bold text-xs border border-emerald-200">
                            {Number(r.quantity_bags)} bags
                          </span>
                        </div>

                        {/* Mobile Expanded Drawer */}
                        {isExpanded && (
                          <div className="pt-2.5 mt-2 border-t border-slate-100 space-y-2 text-xs">
                            <div className="grid grid-cols-2 gap-2 bg-slate-50 p-2.5 rounded-lg">
                              <div>
                                <span className="text-[10px] text-slate-400 uppercase font-bold block">Product</span>
                                <span className="text-slate-800">{r.feed_products?.product_name}</span>
                              </div>
                              <div>
                                <span className="text-[10px] text-slate-400 uppercase font-bold block">Unit Price</span>
                                <span className="font-mono text-slate-800">₱{Number(r.unit_price).toFixed(2)}</span>
                              </div>
                            </div>

                            <div className="flex items-center justify-between pt-1">
                              {hasDoc ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewDoc(r.documents![0]);
                                  }}
                                  className="text-xs text-emerald-700 font-semibold flex items-center gap-1"
                                >
                                  <Paperclip className="w-3.5 h-3.5" /> View Slip
                                </button>
                              ) : (
                                <span className="text-[10px] text-slate-400">No Slip</span>
                              )}

                              {isOperations && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setVoidModalState({
                                      isOpen: true,
                                      tableName: 'feed_receipts',
                                      recordId: r.id,
                                      recordIdentifier: `Receipt #${r.receipt_number || r.invoice_no || r.id}`,
                                    });
                                  }}
                                  className="text-xs text-rose-600 font-semibold flex items-center gap-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Void
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* 5. Tab 2 Content: Cycle-to-Cycle Feed Transfers */}
        {activeTab === 'transfers' && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-slate-400 text-xs font-medium">
                Loading feed transfers from live database...
              </div>
            ) : filteredTransfers.length === 0 ? (
              <div className="p-12 text-center text-slate-500 text-xs">
                No cycle-to-cycle feed transfers found matching the filter criteria.
              </div>
            ) : (
              <>
                {/* Desktop 8-Column Progressive Disclosure Table */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse font-sans">
                    <thead className="bg-slate-100 text-slate-700 font-semibold border-b border-slate-200">
                      <tr>
                        <th className="py-2.5 px-3 whitespace-nowrap w-24">Date</th>
                        <th className="py-2.5 px-3">Source Cycle (Credit)</th>
                        <th className="py-2.5 px-3">Destination Cycle (Debit)</th>
                        <th className="py-2.5 px-3">Feed Product</th>
                        <th className="py-2.5 px-3 text-right whitespace-nowrap">Bags</th>
                        <th className="py-2.5 px-3 text-right whitespace-nowrap">Unit Cost</th>
                        <th className="py-2.5 px-3 text-right whitespace-nowrap">Total Cost</th>
                        <th className="py-2.5 px-2 text-center w-12">Detail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredTransfers.map((t) => {
                        const isExpanded = expandedTransferId === t.id;

                        return (
                          <React.Fragment key={t.id}>
                            <tr
                              onClick={() => setExpandedTransferId(isExpanded ? null : t.id)}
                              className={`cursor-pointer transition-colors ${
                                isExpanded ? 'bg-purple-50/60' : 'hover:bg-slate-50'
                              }`}
                            >
                              <td className="py-2.5 px-3 font-mono text-slate-600 whitespace-nowrap">
                                {formatDate(t.transfer_date)}
                              </td>
                              <td className="py-2.5 px-3">
                                <span className="font-mono font-semibold text-slate-900 block">
                                  {t.source_cycle?.cycle_code || 'Source Cycle'}
                                </span>
                                <span className="text-[11px] text-slate-500 block truncate max-w-[160px]">
                                  {t.source_cycle?.growers?.full_name || 'Company-Owned'}
                                </span>
                              </td>
                              <td className="py-2.5 px-3">
                                <span className="font-mono font-semibold text-purple-900 block">
                                  {t.destination_cycle?.cycle_code || 'Dest. Cycle'}
                                </span>
                                <span className="text-[11px] text-slate-500 block truncate max-w-[160px]">
                                  {t.destination_cycle?.growers?.full_name || 'Company-Owned'}
                                </span>
                              </td>
                              <td className="py-2.5 px-3 text-slate-700 truncate max-w-[160px]">
                                {t.feed_products?.product_name || 'Feed Product'}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900 whitespace-nowrap">
                                {Number(t.quantity_bags).toLocaleString()}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono text-slate-700 whitespace-nowrap">
                                ₱{Number(t.unit_price).toFixed(2)}
                              </td>
                              <td className="py-2.5 px-3 text-right font-mono font-bold text-purple-800 whitespace-nowrap">
                                {formatCurrency(Number(t.total_transferred_cost))}
                              </td>
                              <td className="py-2.5 px-2 text-center text-slate-400">
                                <ChevronDown
                                  className={`w-4 h-4 mx-auto transition-transform duration-150 ${
                                    isExpanded ? 'transform rotate-180 text-purple-700' : ''
                                  }`}
                                />
                              </td>
                            </tr>

                            {/* Progressive Disclosure Row Drawer */}
                            {isExpanded && (
                              <tr className="bg-slate-50/90 border-b border-slate-200">
                                <td colSpan={8} className="p-3.5 sm:p-4">
                                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs bg-white p-3 rounded-lg border border-slate-200 shadow-inner">
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Lineage Reference</span>
                                      <span className="font-mono text-slate-700">
                                        {t.source_feed_receipt
                                          ? `Receipt #${t.source_feed_receipt.receipt_number} (Inv #${t.source_feed_receipt.invoice_no || '—'})`
                                          : 'Weighted Average Inventory'}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Gross Transferred Weight</span>
                                      <span className="font-mono text-slate-800">
                                        {(Number(t.quantity_bags) * 50).toLocaleString()} kg
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-slate-400 block text-[10px] uppercase font-bold">Notes</span>
                                      <span className="text-slate-600">{t.notes || '—'}</span>
                                    </div>
                                  </div>

                                  {isOperations && (
                                    <div className="flex items-center justify-end mt-2 pt-2 border-t border-slate-200">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setVoidModalState({
                                            isOpen: true,
                                            tableName: 'feed_transfers',
                                            recordId: t.id,
                                            recordIdentifier: `Transfer of ${t.quantity_bags} bags to ${t.destination_cycle?.cycle_code}`,
                                          });
                                        }}
                                        className="text-xs h-7 text-rose-600 hover:text-rose-700 hover:bg-rose-50 gap-1"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" /> Soft-Void Transfer
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Responsive Cards Layout */}
                <div className="md:hidden divide-y divide-slate-200">
                  {filteredTransfers.map((t) => {
                    const isExpanded = expandedTransferId === t.id;

                    return (
                      <div
                        key={t.id}
                        onClick={() => setExpandedTransferId(isExpanded ? null : t.id)}
                        className="p-3.5 space-y-2 hover:bg-slate-50 cursor-pointer"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-mono text-slate-500">{formatDate(t.transfer_date)}</span>
                          <span className="text-sm font-bold font-mono text-purple-800">
                            {formatCurrency(Number(t.total_transferred_cost))}
                          </span>
                        </div>

                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-xs font-bold text-slate-900">
                              {t.source_cycle?.cycle_code} → {t.destination_cycle?.cycle_code}
                            </p>
                            <p className="text-xs text-slate-600">{t.feed_products?.product_name}</p>
                          </div>
                          <span className="px-2 py-1 bg-purple-50 text-purple-800 rounded font-mono font-bold text-xs border border-purple-200">
                            {Number(t.quantity_bags)} bags
                          </span>
                        </div>

                        {/* Mobile Expanded Drawer */}
                        {isExpanded && (
                          <div className="pt-2.5 mt-2 border-t border-slate-100 space-y-2 text-xs">
                            <div className="bg-slate-50 p-2.5 rounded-lg space-y-1">
                              <p className="text-[11px] text-slate-500">
                                <strong>Unit Price:</strong> ₱{Number(t.unit_price).toFixed(2)} / bag
                              </p>
                              {t.notes && <p className="text-[11px] text-slate-500"><strong>Notes:</strong> {t.notes}</p>}
                            </div>

                            {isOperations && (
                              <div className="flex justify-end pt-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setVoidModalState({
                                      isOpen: true,
                                      tableName: 'feed_transfers',
                                      recordId: t.id,
                                      recordIdentifier: `Transfer of ${t.quantity_bags} bags to ${t.destination_cycle?.cycle_code}`,
                                    });
                                  }}
                                  className="text-xs text-rose-600 font-semibold flex items-center gap-1"
                                >
                                  <Trash2 className="w-3.5 h-3.5" /> Void Transfer
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Write Modals */}
      <BulkFeedInvoiceModal
        isOpen={showBulkInvoiceModal}
        onClose={() => setShowBulkInvoiceModal(false)}
        onSuccess={loadData}
      />

      <FeedTransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onSuccess={loadData}
      />

      <FeedDeliveryModal
        isOpen={showSingleDeliveryModal}
        onClose={() => setShowSingleDeliveryModal(false)}
        onSuccess={loadData}
      />

      <SoftVoidConfirmModal
        isOpen={voidModalState.isOpen}
        recordIdentifier={voidModalState.recordIdentifier}
        onClose={() => setVoidModalState({ isOpen: false, tableName: 'feed_receipts', recordId: '', recordIdentifier: '' })}
        onConfirm={handleSoftVoidConfirm}
      />

      {previewDoc && (
        <DocumentPreviewModal
          isOpen={true}
          document={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
};
